import { useState, useRef, useEffect } from "react";
import { api, apiErr } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { PageHeader } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Bot, Send, User } from "lucide-react";
import { toast } from "sonner";

const SUGGEST = [
  ["How do I pay by Zelle?", "Comment payer par Zelle ?"],
  ["Where do I see my child's report card?", "Où voir le bulletin de mon enfant ?"],
  ["How do I register a child?", "Comment inscrire un enfant ?"],
  ["How do I upload a document?", "Comment téléverser un document ?"],
];

export default function AiAssistant() {
  const { L } = useLang();
  const [sessionId] = useState(() => {
    try {
      const k = localStorage.getItem("icdh_assist_sid");
      if (k) return k;
      const n = `s_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      localStorage.setItem("icdh_assist_sid", n);
      return n;
    } catch { return `s_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`; }
  });
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", text: q }]);
    setBusy(true);
    try {
      const data = await api.post("/assistant/chat", { session_id: sessionId, message: q });
      setMsgs((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
      setMsgs((m) => [...m, { role: "assistant", text: L("Sorry, I'm temporarily unavailable. Please contact the office at +509 37827146.", "Désolé, je suis momentanément indisponible. Veuillez contacter le bureau au +509 37827146.") }]);
    } finally { setBusy(false); }
  };

  return (
    <div className="animate-fade-up" data-testid="parent-assistant">
      <PageHeader title={L("AI Assistant", "Assistant IA")} description={L("Get help using the portal. For financial questions, contact the office.", "Obtenez de l'aide pour utiliser le portail. Pour les questions financières, contactez le bureau.")} />
      <div className="bg-card border border-border rounded-xl flex flex-col h-[60vh]">
        <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="assistant-messages">
          {msgs.length === 0 && (
            <div className="text-center text-muted-foreground py-8">
              <Bot className="h-10 w-10 mx-auto mb-3 text-primary" />
              <p className="font-medium">{L("Hi! I'm the ICDH assistant.", "Bonjour ! Je suis l'assistant ICDH.")}</p>
              <p className="text-sm mb-4">{L("Ask me how to use the portal.", "Demandez-moi comment utiliser le portail.")}</p>
              <div className="flex flex-wrap gap-2 justify-center">
                {SUGGEST.map((s, i) => (
                  <button key={i} onClick={() => send(L(s[0], s[1]))} data-testid={`assistant-suggest-${i}`} className="text-xs px-3 py-1.5 rounded-full border border-border hover:bg-secondary transition-colors">{L(s[0], s[1])}</button>
                ))}
              </div>
            </div>
          )}
          {msgs.map((m, i) => (
            <div key={i} className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`assistant-msg-${m.role}`}>
              {m.role === "assistant" && <span className="h-8 w-8 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0"><Bot className="h-4 w-4" /></span>}
              <div className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground"}`}>{m.text}</div>
              {m.role === "user" && <span className="h-8 w-8 rounded-full bg-accent/20 text-primary flex items-center justify-center shrink-0"><User className="h-4 w-4" /></span>}
            </div>
          ))}
          {busy && (
            <div className="flex gap-2">
              <span className="h-8 w-8 rounded-full bg-primary/10 text-primary flex items-center justify-center"><Bot className="h-4 w-4" /></span>
              <div className="bg-secondary rounded-2xl px-4 py-3">
                <span className="inline-flex gap-1">
                  <i className="h-1.5 w-1.5 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: "0ms" }} />
                  <i className="h-1.5 w-1.5 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: "150ms" }} />
                  <i className="h-1.5 w-1.5 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: "300ms" }} />
                </span>
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>
        <div className="border-t border-border p-3 flex items-center gap-2">
          <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(); }} placeholder={L("Type your question…", "Écrivez votre question…")} data-testid="assistant-input" disabled={busy} />
          <Button onClick={() => send()} disabled={busy || !input.trim()} data-testid="assistant-send"><Send className="h-4 w-4" /></Button>
        </div>
      </div>
      <p className="mt-3 text-xs text-muted-foreground">{L("For financial questions, please contact the office at +509 37827146.", "Pour toute question financière, veuillez contacter le bureau au +509 37827146.")}</p>
    </div>
  );
}
